import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/common/WhatsAppButton';
import ContactSection from '../components/sections/ContactSection';
import { Clock, Shield, Award, MapPin, Users, Wrench, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

const AboutPage = () => {
  const whatsappLink = `https://wa.me/55${companyInfo.whatsapp}?text=Olá! Gostaria de um orçamento.`;

  const valores = [
    {
      icon: Shield,
      title: "Confiança",
      description: "Mais de 20 anos atendendo clientes com transparência e honestidade."
    },
    {
      icon: Award,
      title: "Qualidade",
      description: "Peças originais e compatíveis de alta qualidade em todos os reparos."
    },
    {
      icon: Clock,
      title: "Agilidade",
      description: "Tempo médio de reparo de apenas 2 horas para a maioria dos serviços."
    },
    {
      icon: Users,
      title: "Atendimento",
      description: "Equipe treinada para oferecer o melhor atendimento ao cliente."
    }
  ];

  const diferenciais = [
    "Mais de 20 anos de experiência no mercado",
    "Mesmo endereço há 20 anos - tradição e confiança",
    "Técnicos especializados e certificados",
    "Garantia de 3 meses em todos os serviços",
    "Orçamento gratuito e sem compromisso",
    "Atendimento personalizado",
    "Peças originais e compatíveis de alta qualidade",
    "Localização privilegiada no centro de Osasco"
  ];

  return (
    <>
      <Helmet>
        <title>Quem Somos | Tsw Info - Assistência Técnica em Osasco há 20 anos</title>
        <meta name="description" content="Conheça a Tsw Info, assistência técnica em Osasco há mais de 20 anos no mesmo local. Especialistas em conserto de celulares, notebooks e MacBooks." />
        <meta name="keywords" content="tsw info, assistência técnica osasco, sobre nós, quem somos, conserto celular osasco" />
        <meta property="og:title" content="Quem Somos - Tsw Info Assistência Técnica" />
        <meta property="og:description" content="Há mais de 20 anos oferecendo soluções em informática e assistência técnica em Osasco." />
        <link rel="canonical" href="https://tswinfo.com.br/quem-somos" />
      </Helmet>

      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#2B3990] via-[#1e2a6e] to-[#2B3990] text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-72 h-72 bg-[#8DC63F] rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#8DC63F] rounded-full filter blur-3xl translate-x-1/3 translate-y-1/3"></div>
          </div>

          <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-[#8DC63F]/20 text-[#8DC63F] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MapPin size={16} />
                Há mais de 20 anos no mesmo local
              </div>

              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Quem Somos
              </h1>

              <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Conheça a história da Tsw Info e nossa dedicação em oferecer as melhores soluções em assistência técnica em Osasco.
              </p>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 50L48 45.8C96 41.7 192 33.3 288 29.2C384 25 480 25 576 33.3C672 41.7 768 58.3 864 62.5C960 66.7 1056 58.3 1152 50C1248 41.7 1344 33.3 1392 29.2L1440 25V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0V50Z" fill="white"/>
            </svg>
          </div>
        </section>

        {/* Nossa História */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#2B3990] mb-6">
                    Nossa História
                  </h2>
                  <div className="space-y-4 text-gray-600 leading-relaxed">
                    <p>
                      A <strong className="text-[#2B3990]">Tsw Info</strong> nasceu há mais de <strong className="text-[#8DC63F]">20 anos</strong> com a missão de oferecer soluções em informática e assistência técnica de qualidade para a região de Osasco e Grande São Paulo.
                    </p>
                    <p>
                      Desde o início, nos dedicamos a construir uma relação de confiança com nossos clientes, oferecendo serviços de excelência, preços justos e um atendimento personalizado que faz toda a diferença.
                    </p>
                    <p>
                      Estamos localizados no <strong className="text-[#2B3990]">mesmo endereço há 20 anos</strong>, na Rua Salem Bechara, 185 - Centro de Osasco, o que demonstra nossa solidez e compromisso com a comunidade local.
                    </p>
                    <p>
                      Ao longo dessas duas décadas, acompanhamos a evolução da tecnologia e nos especializamos no conserto de smartphones, tablets, notebooks e MacBooks das principais marcas do mercado.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-[#2B3990] rounded-full flex items-center justify-center">
                      <Wrench size={32} className="text-[#8DC63F]" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#2B3990]">+20 Anos</h3>
                      <p className="text-gray-500">de experiência</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {diferenciais.map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle size={20} className="text-[#8DC63F] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nossos Valores */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#2B3990] mb-4">
                Nossos Valores
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Os pilares que guiam nosso trabalho e nos tornam referência em assistência técnica em Osasco.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {valores.map((valor, index) => (
                <div 
                  key={index}
                  className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="w-20 h-20 bg-[#2B3990] rounded-full flex items-center justify-center mx-auto mb-6">
                    <valor.icon size={36} className="text-[#8DC63F]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2B3990] mb-3">
                    {valor.title}
                  </h3>
                  <p className="text-gray-600">
                    {valor.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Especialidades */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-[#2B3990] mb-6">
                Nossas Especialidades
              </h2>
              <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
                Somos especialistas em conserto e manutenção de dispositivos eletrônicos das principais marcas do mercado.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {['iPhone', 'Samsung', 'MacBook', 'Notebooks', 'Motorola', 'Xiaomi'].map((marca) => (
                  <div key={marca} className="p-6 bg-gray-50 rounded-xl hover:bg-[#2B3990] group transition-colors cursor-pointer">
                    <h3 className="font-semibold text-[#2B3990] group-hover:text-white transition-colors">
                      {marca}
                    </h3>
                    <p className="text-sm text-gray-500 group-hover:text-gray-200 transition-colors">
                      Assistência Técnica
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#8DC63F]">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Venha nos conhecer!
              </h2>
              <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
                Estamos há 20 anos no mesmo endereço, prontos para atender você com qualidade e agilidade.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-[#2B3990] hover:bg-[#1e2a6e] text-white font-semibold px-8 py-6 text-lg">
                    <Phone size={20} className="mr-2" />
                    Chamar no WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <ContactSection />
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default AboutPage;
